/**
 * Phase 3 — Reverse Optimisation Engine API client.
 *
 * Thin axios wrappers that resolve to the response body. Mirrors the
 * conventions used by ``src/api/endpoints.js`` so consumers can keep
 * a uniform call style across the operator UI.
 */
import { api as client } from "@/api/client";

const BASE = "/api/optimisation";

export const optimisation = {
    listVariables: () =>
        client.get(`${BASE}/variables`).then((r) => r.data),

    upsertVariable: (key, body) =>
        client.put(`${BASE}/variables/${key}`, body).then((r) => r.data),

    deleteVariable: (key) =>
        client.delete(`${BASE}/variables/${key}`).then((r) => r.data),

    validateStudy: (body) =>
        client.post(`${BASE}/validate`, body).then((r) => r.data),

    createStudy: (body) =>
        client.post(`${BASE}/studies`, body).then((r) => r.data),

    listStudies: (params = {}) =>
        client.get(`${BASE}/studies`, { params }).then((r) => r.data),

    getStudy: (id) =>
        client.get(`${BASE}/studies/${id}`).then((r) => r.data),

    cancelStudy: (id) =>
        client.post(`${BASE}/studies/${id}/cancel`).then((r) => r.data),

    deleteStudy: (id) =>
        client.delete(`${BASE}/studies/${id}`).then((r) => r.data),

    listCandidates: (id, { limit = 200, skip = 0, status, sort } = {}) => {
        const params = { limit, skip };
        if (status) params.status = status;
        if (sort) params.sort = sort;
        return client
            .get(`${BASE}/studies/${id}/candidates`, { params })
            .then((r) => r.data);
    },

    listEvents: (id, { limit = 100, skip = 0, since } = {}) => {
        const params = { limit, skip };
        if (since) params.since = since;
        return client
            .get(`${BASE}/studies/${id}/events`, { params })
            .then((r) => r.data);
    },

    listArtifacts: (id) =>
        client.get(`${BASE}/studies/${id}/artifacts`).then((r) => r.data),

    artifactDownloadUrl: (id, name) => {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        return `${base}${BASE}/studies/${id}/artifacts/${encodeURIComponent(name)}`;
    },

    downloadArtifact: (id, name) =>
        client
            .get(`${BASE}/studies/${id}/artifacts/${encodeURIComponent(name)}`, {
                responseType: "text",
            })
            .then((r) => r.data),

    getDiff: (id) =>
        client.get(`${BASE}/studies/${id}/diff`).then((r) => r.data),

    promoteCandidate: (id, { candidate_id, target }) =>
        client
            .post(`${BASE}/studies/${id}/promote`, { candidate_id, target })
            .then((r) => r.data),

    // Phase 4b — Design Optimisation Report (PDF + DOCX).
    generateReport: (id, formats) =>
        client
            .post(
                `${BASE}/studies/${id}/report`,
                formats ? { formats } : {},
            )
            .then((r) => r.data),

    // Phase 3.5 — tenant-resolved settings + failure-mode weights.
    getSettings: () =>
        client.get(`${BASE}/settings`).then((r) => r.data),

    listFailureModeWeights: () =>
        client.get(`${BASE}/failure-mode-weights`).then((r) => r.data),
};

export default optimisation;
